import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";

const CashFlowReportPrint = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const params = {
        start_date: searchParams.get("start_date") || "",
        end_date: searchParams.get("end_date") || "",
        master_cash_id: searchParams.get("master_cash_id") || undefined,
    };

    const { data, isLoading, error } = useFetchData(
        "report/monthly-cash-flow",
        params,
    );

    const reportData = data?.transactions || [];
    const openingBalance = data?.opening_balance || 0;
    const closingBalance = data?.closing_balance || 0;
    const period = data?.period || {};
    const totals = data?.totals || { debit: 0, credit: 0 };

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/cash-flow-monthly-report")}
                >
                    Kembali
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/cash-flow-monthly-report")}
                >
                    Kembali
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Laporan_Arus_Kas_${period.start || ""}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="LAPORAN ARUS KAS" />

                <div className="mb-2 text-xs">
                    <span className="font-medium">Periode: </span>
                    {period.start || "-"} s/d {period.end || "-"}
                </div>

                <table className="mb-6 w-full border-collapse border border-black text-xs">
                    <thead>
                        <tr className="bg-default-100 whitespace-nowrap">
                            <th className="border border-black px-1 py-px text-center">
                                Tgl. Transaksi
                            </th>
                            <th className="border border-black px-1 py-px text-left">
                                Nama Kas
                            </th>
                            <th className="border border-black px-1 py-px text-left">
                                Keterangan
                            </th>
                            <th className="border border-black px-1 py-px text-right">
                                Debet
                            </th>
                            <th className="border border-black px-1 py-px text-right">
                                Kredit
                            </th>
                            <th className="border border-black px-1 py-px text-right">
                                Saldo
                            </th>
                        </tr>
                        {/* Opening Balance */}
                        <tr className="bg-default-50">
                            <th
                                colSpan={3}
                                className="border border-black px-1 py-px text-left font-bold"
                            >
                                SALDO AWAL
                            </th>
                            <th className="border border-black px-1 py-px"></th>
                            <th className="border border-black px-1 py-px"></th>
                            <th className="border border-black px-1 py-px text-right font-bold">
                                {formatCurrency(openingBalance)}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="border border-black p-4 text-center text-default-400"
                                >
                                    Tidak ada transaksi pada periode ini
                                </td>
                            </tr>
                        ) : (
                            reportData.map((item, index) => (
                                <tr key={index}>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {formatDate(
                                            item.transaction_date,
                                            "dd MMM yyyy",
                                        )}
                                    </td>
                                    <td className="border border-black px-1 py-px whitespace-nowrap">
                                        {item.cash_name}
                                    </td>
                                    <td className="border border-black px-1 py-px">
                                        {item.description ||
                                            item.category_name}
                                        {item.is_transfer &&
                                            (item.is_income
                                                ? ` (Dari: ${item.transfer_source_cash_name || "Unknown"})`
                                                : ` (Ke: ${item.transfer_dest_cash_name || "Unknown"})`)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right whitespace-nowrap">
                                        {item.debit > 0
                                            ? formatCurrency(item.debit)
                                            : "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right whitespace-nowrap">
                                        {item.credit > 0
                                            ? formatCurrency(item.credit)
                                            : "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right whitespace-nowrap">
                                        {formatCurrency(item.balance)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="bg-default-100 font-bold">
                            <td
                                colSpan={3}
                                className="border border-black px-1 py-px text-center"
                            >
                                TOTAL
                            </td>
                            <td className="border border-black px-1 py-px text-right">
                                {formatCurrency(totals.debit)}
                            </td>
                            <td className="border border-black px-1 py-px text-right">
                                {formatCurrency(totals.credit)}
                            </td>
                            <td className="border border-black px-1 py-px text-right">
                                {formatCurrency(closingBalance)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* Footer */}
                <div className="mt-8 text-xs text-default-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CashFlowReportPrint;
